import { useEffect, useState } from "react";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";

// API
import { 
    getClientAssignmentsAPI, 
    createAssignmentAPI, 
    updateAssignmentAPI,
    deleteAssignmentAPI, 
    getEmployees,
} from "api/apiHelper";

// Components
import Table from "components/Table";
import Card from "components/Card";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";

// --- Interfaces ---
interface Assignment {
    id: number;
    employee_id: number;
    employee_name: string;
    employee_email: string;
    assignment_type: 'HOURLY' | 'PROJECT_BASIS' | 'FULL_TIME' | 'PART_TIME';
    pricing: string;
    start_date: string;
    end_date: string | null;
    is_active: boolean;
}

interface Employee {
    id: number;
    full_name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    official_email?: string;
}

interface Props {
    clientId: string; 
}

const ClientEmployeeAssignment = ({ clientId }: Props) => {
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null); // ✅ Tracks Edit Mode

    const [formData, setFormData] = useState({
        employee_id: "",
        assignment_type: "FULL_TIME",
        pricing: "",
        start_date: new Date().toISOString().split('T')[0],
        end_date: "",
        is_active: true
    });

    const columnHelper = createColumnHelper<Assignment>();

    // --- Fetch Data ---
    const fetchData = async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const res = await getClientAssignmentsAPI(clientId);
            const data = res.data.results || res.data; // Extract data first
            
            setAssignments(data);
            console.log("Fetched Data:", data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const res = await getEmployees(true);
            const employeesList = Array.isArray(res.data) ? res.data : res.data?.results || res.data;
            setEmployees(employeesList || []);
        } catch (error) {
            console.error("Failed to load employees", error);
        }
    };

    useEffect(() => {
        fetchData();
        fetchEmployees(); // Pre-load employees
    }, [clientId]);

    // --- Handlers ---
    
    // Open Modal for Creating
    const handleOpenCreate = () => {
        setEditingId(null); // Reset to Create Mode
        setFormData({
            employee_id: "",
            assignment_type: "FULL_TIME",
            pricing: "",
            start_date: new Date().toISOString().split('T')[0],
            end_date: "",
            is_active: true
        });
        setIsModalOpen(true);
    };

    // Open Modal for Editing
    const handleOpenEdit = (row: Assignment) => {
        setEditingId(row.id);
        setFormData({
            employee_id: row.employee_id ? String(row.employee_id) : "",
            assignment_type: row.assignment_type,
            pricing: row.pricing,
            start_date: row.start_date,
            end_date: row.end_date || "",
            is_active: row.is_active ?? true
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if(!window.confirm("Are you sure you want to remove this employee from the project?")) return;
        try {
            await deleteAssignmentAPI(id);
            toast.success("Assignment removed");
            fetchData();
        } catch (error) {
            toast.error("Failed to remove assignment");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Prepare payload (convert empty date to null)
            const payload = {
                ...formData,
                client_id: clientId,
                end_date: formData.end_date ? formData.end_date : null 
            };

            if (editingId) {
                await updateAssignmentAPI(editingId, payload);
                toast.success("Assignment Updated Successfully!");
            } else {
                await createAssignmentAPI({ ...payload, is_active: true });
                toast.success("Employee Assigned Successfully!");
            }
            
            setIsModalOpen(false);
            fetchData();
        } catch (error: any) {
            toast.error(error.response?.data?.non_field_errors?.[0] || "Failed to save assignment");
        }
    };

    // --- Table Columns ---
    const columns: ColumnDef<Assignment, any>[] = [
        columnHelper.accessor("employee_name", {
            header: () => <TableHeader>Employee</TableHeader>,
            cell: (info) => (
                <TableCell>
                    <div className="flex flex-col">
                        <span className="font-bold text-navy-700 dark:text-white">
                            {info.getValue()}
                        </span>
                        <span className="text-xs text-gray-500">{info.row.original.employee_email}</span>
                    </div>
                </TableCell>
            )
        }),
        columnHelper.accessor("assignment_type", {
            header: () => <TableHeader>Type</TableHeader>,
            cell: (info) => {
                const type = info.getValue();
                let color = "bg-gray-100 text-gray-600";
                if(type === 'FULL_TIME') color = "bg-blue-100 text-blue-600";
                if(type === 'PROJECT_BASIS') color = "bg-purple-100 text-purple-600";
                if(type === 'HOURLY') color = "bg-orange-100 text-orange-600";
                if(type === 'PART_TIME') color = "bg-green-100 text-orange-600";

                return (
                    <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${color}`}>
                            {type.replace("_", " ")}
                        </span>
                    </TableCell>
                );
            }
        }),
        columnHelper.accessor("is_active", {
            header: () => <TableHeader>Status</TableHeader>,
            cell: (info) => (
                <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${info.getValue() ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {info.getValue() ? "Active" : "Inactive"}
                    </span>
                </TableCell>
            )
        }),
        columnHelper.accessor("pricing", {
            header: () => <TableHeader>Rate/Salary</TableHeader>,
            cell: (info) => <TableCell className="font-mono font-medium text-brand-500">${info.getValue()}</TableCell>
        }),
        columnHelper.accessor("start_date", {
            header: () => <TableHeader>Dates</TableHeader>,
            cell: (info) => (
                <TableCell>
                    <div className="text-sm">
                        <div className="text-gray-700 dark:text-white">Start: {info.getValue()}</div>
                        {info.row.original.end_date ? (
                             <div className="text-red-500 text-xs">End: {info.row.original.end_date}</div>
                        ) : (
                            <div className="text-green-500 text-xs">Ongoing</div>
                        )}
                    </div>
                </TableCell>
            )
        }),
        columnHelper.display({
            id: "actions",
            header: () => <TableHeader>Action</TableHeader>,
            cell: (info) => (
                <TableCell>
                    <div className="flex gap-2">
                        <button 
                            onClick={() => handleOpenEdit(info.row.original)}
                            className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                            title="Edit Assignment"
                        >
                            <i className="fas fa-pen"></i>
                        </button>

                        {/* Delete Button */}
                        <button 
                            onClick={() => handleDelete(info.row.original.id)}
                            className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                            title="Remove Assignment"
                        >
                            <i className="fas fa-trash-alt"></i>
                        </button>
                    </div>
                </TableCell>
            )
        })
    ];

    return (
        <Card extra="w-full p-4 mt-5">
            <div className="flex justify-between items-center mb-4 px-2">
                <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                    Assigned Staff
                </h4>
                <button 
                    onClick={handleOpenCreate}
                    className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                    <i className="fas fa-plus"></i> Assign Employee
                </button>
            </div>

            <Table 
                tableData={assignments} 
                columns={columns} 
                tableHeading="Staff List"
            />

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-navy-800 w-full max-w-lg rounded-xl shadow-2xl p-6">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                                {editingId ? "Edit Assignment" : "Assign New Employee"}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-red-500">
                                <i className="fas fa-times text-lg"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Employee</label>
                                <select 
                                    required
                                    disabled={!!editingId}
                                    className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-navy-600 dark:bg-navy-700 dark:text-white focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50 disabled:bg-gray-100"
                                    value={formData.employee_id}
                                    onChange={e => setFormData({...formData, employee_id: e.target.value})}
                                >
                                    <option value="">-- Choose Employee --</option>
                                    {employees.map(emp => {
                                        const displayName = emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || "Unknown Name";
                                        const displayEmail = emp.email || emp.official_email || "";
                                        return (
                                            <option key={emp.id} value={emp.id}>
                                                {displayName} {displayEmail ? `(${displayEmail})` : ""}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            {/* Type & Price Row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assignment Type</label>
                                    <select 
                                        className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                        value={formData.assignment_type}
                                        onChange={e => setFormData({...formData, assignment_type: e.target.value})}
                                    >
                                        <option value="FULL_TIME">Full Time</option>
                                        <option value="HOURLY">Hourly</option>
                                        <option value="PROJECT_BASIS">Project Basis</option>
                                        <option value="PART_TIME">Part Time</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate / Salary</label>
                                    <input 
                                        type="number" 
                                        required
                                        min="0"
                                        step="0.01"
                                        placeholder="0.00"
                                        className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                        value={formData.pricing}
                                        onChange={e => setFormData({...formData, pricing: e.target.value})}
                                    />
                                </div>
                            </div>

                            {/* Dates Row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                                    <input 
                                        type="date" 
                                        required
                                        className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                        value={formData.start_date}
                                        onChange={e => setFormData({...formData, start_date: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date (Optional)</label>
                                    <input 
                                        type="date" 
                                        className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                        value={formData.end_date}
                                        onChange={e => setFormData({...formData, end_date: e.target.value})}
                                    />
                                </div>
                            </div>

                            {editingId && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                    <select 
                                        className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                        value={String(formData.is_active)}
                                        onChange={e => setFormData({...formData, is_active: e.target.value === 'true'})}
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                            )}

                            {/* Footer Buttons */}
                            <div className="flex justify-end gap-3 mt-6">
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="px-6 py-2 bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg shadow-sm transition-all"
                                >
                                    {editingId ? "Update Assignment" : "Confirm Assignment"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default ClientEmployeeAssignment;